<template>
  <div class="test-room">
   <a-row :gutter="[14, 14]">
      <a-col :xs="24" :xl="12">
        <a-card class="custom-card panel-card">
          <template #title>
            <div class="title-row">
              <div>
                <div class="test-title">{{ title }}</div>
                <div class="test-subtitle">
                  Savol {{ currentIndex + 1 }} / {{ questions.length }}
                  <span class="mistake-badge" :class="{ 'mistake-limit': wrongCount >= 2 }">
                    Xatolar: {{ wrongCount }} / 3
                  </span>
                </div>
              </div>
              <a-tag color="blue">F1-F{{ currentQuestion?.options?.length || 6 }}</a-tag>
            </div>
          </template>

          <h2 class="question-title">{{ currentQuestion?.text }}</h2>

          <div class="options">
            <a-button
              v-for="(option, idx) in currentQuestion?.options || []"
              :key="option.id"
              block
              class="option-btn"
              :class="{
                'option-correct': showExplanation && option.is_correct,
                'option-wrong': showExplanation && 
                  selectedAnswerId === option.id && 
                  !option.is_correct
              }"
              :type="selectedAnswerId === option.id ? 'primary' : 'default'"
              @click="selectAnswer(option.id, idx)"
              :disabled="showExplanation || isFinished"
            >
              <span class="option-hotkey">F{{ idx + 1 }}</span>
              <span class="option-text">{{ option.text }}</span>
              <CheckCircleFilled 
                v-if="showExplanation && option.is_correct" 
                class="correct-icon" 
              />
              <CloseCircleFilled 
                v-else-if="showExplanation && selectedAnswerId === option.id && !option.is_correct" 
                class="wrong-icon" 
              />
            </a-button>
          </div>
        </a-card>
      </a-col>

      <a-col :xs="24" :xl="12">
        <a-card class="custom-card panel-card">
          <div class="image-wrap">
            <img 
              :src="getImageUrl(currentQuestion?.image_url)" 
              class="question-image" 
              alt="question" 
              @error="handleImageError"
            />
          </div>

          <div v-if="showExplanation" class="explanation">
            <div class="explanation-title">
              <BulbOutlined /> Izoh
            </div>
            <p>{{ currentQuestion?.explanation || 'Izoh mavjud emas' }}</p>
            <a-alert
              :type="selectedAnswerId ? (isCurrentAnswerCorrect ? 'success' : 'error') : 'info'"
              show-icon
            >
              <template #message>
                <span v-if="selectedAnswerId && isCurrentAnswerCorrect">
                  <CheckCircleOutlined /> To‘g‘ri javob! 
                  <span v-if="currentQuestion?.previous_wrong_answer" class="success-note">
                    (Xato savollar ro'yxatidan o'chirildi)
                  </span>
                </span>
                <span v-else-if="selectedAnswerId && !isCurrentAnswerCorrect">
                  <CloseCircleOutlined /> Noto‘g‘ri javob
                </span>
                <span v-else>
                  <InfoCircleOutlined /> Javob tanlanmadi
                </span>
              </template>
            </a-alert>

            <!-- Previous wrong answer info -->
            <div v-if="currentQuestion?.previous_wrong_answer" class="previous-wrong">
              <InfoCircleOutlined /> Oldingi xato javobingiz: 
              <strong>{{ currentQuestion.previous_wrong_answer.text }}</strong>
            </div>
          </div>

        
        </a-card>
      </a-col>
    </a-row>

    <!-- Question navigation -->
    <div class="fixed-bottom-bar">
      <div class="question-number-grid">
        <button
          v-for="(q, index) in questions"
          :key="q.id"
          class="question-btn"
          :class="getQuestionBtnClass(index)"
          @click="goToQuestion(index)"
          :disabled="!showExplanation || isFinished"
        >
          {{ index + 1 }}
          <span v-if="wrongMap[index]" class="btn-badge">!</span>
        </button>
      </div>
    </div>

    <!-- Finish confirm modal -->
    <a-modal
      v-model:visible="showFinishConfirm"
      title="Testni yakunlash"
      @ok="submitFinish"
      @cancel="showFinishConfirm = false"
      ok-text="Yakunlash"
      cancel-text="Bekor qilish"
      :confirm-loading="finishing"
    >
      <p>Testni yakunlamoqchimisiz?</p>
      <p><strong>Natijalar:</strong></p>
      <ul>
        <li>To'g'ri javoblar: {{ correctCount }}</li>
        <li>Noto'g'ri javoblar: {{ wrongCount }}</li>
        <li>Javob berilmagan: {{ unansweredCountComputed }}</li>
      </ul>
    </a-modal>
  </div>
</template>

<script>
import { 
  Modal, 
  message 
} from 'ant-design-vue'
import {
  BulbOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  InfoCircleOutlined
} from '@ant-design/icons-vue'
import axios from 'axios'

export default {
  name: 'RealExamRoom',
  components: {
    BulbOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    CheckCircleFilled,
    CloseCircleFilled,
    InfoCircleOutlined
  },
  props: {
    title: String,
    questions: {
      type: Array,
      default: () => []
    },
    resultId: {
      type: Number,
      required: true
    },
    backRoute: {
      type: String,
      default: '/real-exam'
    }
  },

  data() {
    return {
      currentIndex: 0,
      selectedAnswerId: null,
      showExplanation: false,
      answeredMap: {},
      wrongMap: {},
      correctCount: 0,
      wrongCount: 0,
      isFinished: false,
      finishing: false,
      showFinishConfirm: false,
      startTime: Date.now(),
      timeLeft: 3600, // 60 minutes in seconds
      timerInterval: null,
      answers: [], // Store answers for final submission
      autoNextTimeout: null
    }
  },

  computed: {
    currentQuestion() {
      return this.questions[this.currentIndex] || {}
    },

    totalQuestions() {
      return this.questions.length
    },

    unansweredCountComputed() {
      return this.totalQuestions - this.correctCount - this.wrongCount
    },

    isCurrentAnswerCorrect() {
      if (!this.selectedAnswerId || !this.currentQuestion?.options) return false
      const selectedOption = this.currentQuestion.options.find(opt => opt.id === this.selectedAnswerId)
      return selectedOption ? selectedOption.is_correct : false
    }
  },

  mounted() {
    this.startTimer()
    window.addEventListener('keydown', this.handleKeydown)
    
    // Initialize answered map
    this.questions.forEach((_, index) => {
      this.answeredMap[index] = false
      this.wrongMap[index] = false
    })
  },

  beforeUnmount() {
    window.removeEventListener('keydown', this.handleKeydown)
    if (this.timerInterval) {
      clearInterval(this.timerInterval)
    }
    if (this.autoNextTimeout) {
      clearTimeout(this.autoNextTimeout)
    }
  },

  methods: {
    startTimer() {
      this.timerInterval = setInterval(() => {
        if (!this.isFinished) {
          this.timeLeft -= 1
          if (this.timeLeft <= 0) {
            this.autoFinishTest()
          }
        }
      }, 1000)
    },

    formatTime(seconds) {
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    },

    handleKeydown(e) {
      if (this.showExplanation || this.isFinished) return

      const key = e.key
      if (key.startsWith('F') && !isNaN(key.slice(1))) {
        e.preventDefault()
        const index = parseInt(key.slice(1)) - 1
        const option = this.currentQuestion?.options?.[index]
        if (option) {
          this.selectAnswer(option.id, index)
        }
      }
    },

    selectAnswer(optionId, optionIndex) {
      if (this.showExplanation || this.isFinished) return

      Modal.confirm({
        title: 'Javobni tasdiqlash',
        content: `Siz F${optionIndex + 1} variantini tanladingiz. Tasdiqlaysizmi?`,
        okText: 'Tasdiqlash',
        cancelText: 'Bekor qilish',
        centered: true,
        onOk: () => {
          this.processAnswer(optionId)
        }
      })
    },

    processAnswer(optionId) {
      this.selectedAnswerId = optionId
      this.showExplanation = true
      this.answeredMap[this.currentIndex] = true

      const selectedOption = this.currentQuestion.options.find(opt => opt.id === optionId)
      const isCorrect = selectedOption ? selectedOption.is_correct : false

      // Store answer
      this.answers.push({
        question_id: this.currentQuestion.id,
        selected_option_id: optionId,
        is_correct: isCorrect
      })

      if (isCorrect) {
        this.correctCount++
        message.success('To‘g‘ri javob!')
      } else {
        this.wrongCount++
        this.wrongMap[this.currentIndex] = true
        message.error('Noto‘g‘ri javob!')

        // Auto-finish if 3 mistakes
        if (this.wrongCount >= 3) {
          setTimeout(() => {
            if (!this.isFinished) {
              this.finishTest()
            }
          }, 1500)
          return
        }
      }

      // If it's a wrong question from wrong questions list, resolve it
      if (!isCorrect && this.currentQuestion.wrong_question_id) {
        this.resolveWrongQuestion(this.currentQuestion.wrong_question_id, optionId)
      }

      // Auto move to next question after 1.5 seconds
      this.autoNextTimeout = setTimeout(() => {
        if (this.currentIndex < this.questions.length - 1) {
          this.nextQuestion()
        } else if (this.currentIndex === this.questions.length - 1) {
          // If last question, finish test automatically
          this.finishTest()
        }
      }, 1500)
    },

    async resolveWrongQuestion(wrongQuestionId, selectedOptionId) {
      try {
        const token = localStorage.getItem('access_token')
        
        await axios.post(
          `/api/v1/tests/wrong-question-resolve/${wrongQuestionId}/`,
          { selected_option_id: selectedOptionId },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        )
      } catch (err) {
        console.error('Error resolving wrong question:', err)
      }
    },

    nextQuestion() {
      // Clear any pending auto-next timeout
      if (this.autoNextTimeout) {
        clearTimeout(this.autoNextTimeout)
        this.autoNextTimeout = null
      }

      if (this.currentIndex < this.questions.length - 1) {
        this.currentIndex++
        this.selectedAnswerId = null
        this.showExplanation = false
      }
    },

    goToQuestion(index) {
      if (!this.showExplanation || this.isFinished) return
      
      // Clear any pending auto-next timeout
      if (this.autoNextTimeout) {
        clearTimeout(this.autoNextTimeout)
        this.autoNextTimeout = null
      }

      this.currentIndex = index
      this.selectedAnswerId = null
      this.showExplanation = false
    },

    finishTest() {
      // Clear any pending auto-next timeout
      if (this.autoNextTimeout) {
        clearTimeout(this.autoNextTimeout)
        this.autoNextTimeout = null
      }

      // If all questions answered, directly submit
      if (this.answers.length === this.questions.length) {
        this.submitFinish()
      } else {
        this.showFinishConfirm = true
      }
    },

    async submitFinish() {
      this.finishing = true
      this.showFinishConfirm = false

      try {
        const durationSeconds = Math.floor((Date.now() - this.startTime) / 1000)
        const token = localStorage.getItem('access_token')

        const response = await axios.post(
          '/api/v1/tests/finish/',
          {
            result_id: this.resultId,
            duration_seconds: durationSeconds,
            answers: this.answers
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        )

        if (response.data) {
          this.isFinished = true
          clearInterval(this.timerInterval)

          // Simple result object without vuex
          const result = {
            title: this.title,
            totalQuestions: this.totalQuestions,
            correctCount: this.correctCount,
            wrongCount: this.wrongCount,
            percent: response.data.result.score_percent,
            failedByMistakes: this.wrongCount >= 3,
            backRoute: this.backRoute,
            resultData: response.data.result
          }

          // Store in sessionStorage instead of vuex
          sessionStorage.setItem('examResult', JSON.stringify(result))

          message.success('Test muvaffaqiyatli yakunlandi!')
          this.$router.push('/result')
        }
      } catch (err) {
        console.error('Finish test error:', err)
        message.error(err.response?.data?.detail || 'Testni yakunlashda xatolik yuz berdi')
      } finally {
        this.finishing = false
      }
    },

    autoFinishTest() {
      if (!this.isFinished) {
        message.warning('Vaqt tugadi! Test avtomatik yakunlanmoqda...')
        this.finishTest()
      }
    },

    getImageUrl(imageUrl) {
      if (!imageUrl) return '/no-image.png'
      if (imageUrl.startsWith('http')) return imageUrl
      if (imageUrl.startsWith('/media/')) return `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'}${imageUrl}`
      return imageUrl
    },

    handleImageError(e) {
      e.target.src = '/no-image.png'
    },

    getQuestionBtnClass(index) {
      return {
        'active': this.currentIndex === index,
        'answered': this.answeredMap[index] && !this.wrongMap[index],
        'wrong': this.wrongMap[index],
        'current': this.currentIndex === index
      }
    }
  }
}
</script>

<style scoped>
.test-room { 
  padding-bottom: 70px; 
  position: relative;
}

.timer-bar {
  margin-bottom: 16px;
}

.timer-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 12px;
}

.timer-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: white;
  padding: 8px 16px;
}

.timer-label {
  font-size: 16px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.timer-value {
  font-size: 24px;
  font-weight: 700;
  font-family: monospace;
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 12px;
  border-radius: 8px;
}

.timer-warning {
  background: rgba(255, 107, 107, 0.3);
  color: #ff6b6b;
  animation: pulse 1s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.7; }
  100% { opacity: 1; }
}

.panel-card { 
  min-height: 500px; 
}

.title-row { 
  display: flex; 
  justify-content: space-between; 
  align-items: center; 
  gap: 10px; 
}

.test-title { 
  font-size: 17px; 
  font-weight: 800; 
  color: var(--text); 
}

.test-subtitle { 
  font-size: 12px; 
  color: var(--text-soft); 
  margin-top: 3px; 
}

.mistake-badge { 
  margin-left: 8px; 
  display: inline-block; 
  padding: 3px 8px; 
  border-radius: 999px; 
  font-size: 11px; 
  font-weight: 700; 
  color: #fecaca; 
  background: rgba(239, 68, 68, 0.12); 
  border: 1px solid rgba(239, 68, 68, 0.25); 
}

.mistake-limit {
  background: rgba(239, 68, 68, 0.25);
  border-color: rgba(239, 68, 68, 0.5);
  animation: shake 0.5s;
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

.question-title { 
  font-size: 21px; 
  line-height: 1.4; 
  margin: 0 0 16px; 
  color: var(--text); 
  font-weight: 700; 
}

.options { 
  display: flex; 
  flex-direction: column; 
  gap: 10px; 
  margin-bottom: 16px;
}

.option-btn { 
  min-height: 46px; 
  height: auto; 
  border-radius: 12px !important; 
  display: flex !important; 
  justify-content: flex-start !important; 
  align-items: center; 
  padding: 10px 12px !important; 
  text-align: left !important; 
  position: relative;
  transition: all 0.3s ease;
}

.option-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.option-correct {
  background: rgba(82, 196, 26, 0.1) !important;
  border-color: #52c41a !important;
}

.option-wrong {
  background: rgba(245, 34, 45, 0.1) !important;
  border-color: #f5222d !important;
}

.option-hotkey { 
  min-width: 36px; 
  height: 28px; 
  display: inline-flex; 
  align-items: center; 
  justify-content: center; 
  border-radius: 8px; 
  margin-right: 10px; 
  font-size: 11px; 
  font-weight: 800; 
  background: rgba(59, 130, 246, 0.16); 
  border: 1px solid var(--border); 
}

.option-text { 
  flex: 1; 
  white-space: normal; 
  line-height: 1.35; 
  font-size: 13px; 
}

.correct-icon, .wrong-icon {
  margin-left: 8px;
  font-size: 16px;
}

.correct-icon {
  color: #52c41a;
}

.wrong-icon {
  color: #f5222d;
}

.image-wrap { 
  width: 100%; 
  min-height: 260px; 
  border-radius: 14px; 
  overflow: hidden; 
  border: 1px solid var(--border); 
  background: rgba(255, 255, 255, 0.02); 
  display: flex; 
  align-items: center; 
  justify-content: center; 
}

.question-image { 
  width: 100%; 
  max-height: 320px; 
  object-fit: contain; 
  display: block; 
}

.explanation { 
  margin-top: 14px; 
  padding: 14px; 
  border-radius: 14px; 
  border: 1px solid var(--border); 
  background: rgba(255, 255, 255, 0.03); 
}

.explanation-title { 
  font-size: 16px; 
  font-weight: 800; 
  margin-bottom: 8px; 
  color: var(--text); 
  display: flex;
  align-items: center;
  gap: 8px;
}

.explanation p { 
  color: var(--text-soft); 
  line-height: 1.6; 
  margin-top: 0; 
  margin-bottom: 12px; 
  font-size: 13px; 
}

.placeholder-box { 
  margin-top: 14px; 
  padding: 14px; 
  border-radius: 14px; 
  border: 1px dashed var(--border); 
  color: var(--text-soft); 
  min-height: 90px; 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  text-align: center; 
  font-size: 13px; 
  gap: 8px;
}

.previous-wrong {
  margin-top: 12px;
  padding: 8px;
  background: rgba(250, 173, 20, 0.1);
  border-radius: 8px;
  font-size: 12px;
  color: #faad14;
  display: flex;
  align-items: center;
  gap: 8px;
}

.success-note {
  font-size: 11px;
  opacity: 0.8;
}

.fixed-bottom-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: var(--bg-secondary);
  border-top: 1px solid var(--border);
  padding: 12px;
  z-index: 100;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.05);
}

.question-number-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 16px;
}

.question-btn {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: 2px solid var(--border);
  background: var(--bg-primary);
  color: var(--text);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.question-btn:hover:not(:disabled) {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
  transform: translateY(-2px);
}

.question-btn.active {
  background: var(--primary);
  color: white;
  border-color: var(--primary);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.question-btn.answered {
  background: rgba(82, 196, 26, 0.15);
  border-color: #52c41a;
  color: #52c41a;
}

.question-btn.wrong {
  background: rgba(245, 34, 45, 0.15);
  border-color: #f5222d;
  color: #f5222d;
}

.question-btn.current {
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
}

.question-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.btn-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  width: 20px;
  height: 20px;
  background: #f5222d;
  color: white;
  border-radius: 50%;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid var(--bg-primary);
  font-weight: bold;
}

/* Responsive */
@media (max-width: 768px) {
  .timer-value {
    font-size: 20px;
  }

  .question-title {
    font-size: 18px;
  }

  .panel-card {
    min-height: auto;
  }

  .question-number-grid {
    gap: 6px;
  }

  .question-btn {
    width: 38px;
    height: 38px;
    font-size: 13px;
  }
}
</style>